<script setup lang="ts">
import __                                               from "../../Utils/tranlation";
import type { TypePaginationData, TypeLinks, TypeLink } from "../../Utils/CommonTypes/qr_code";

type TypeProps = TypePaginationData;

const props = defineProps<TypeProps>(),
    onlyLinks : TypeLinks = [],
    propsLinksLength = props.pagination_data.links.length;

for (let index = 0; index < propsLinksLength; index++) {
    if (index == 0 || index == propsLinksLength - 1) {
        continue;
    }

    onlyLinks.push(props.pagination_data.links[index]);
}

const preparedLinksData : TypeLinks = [],
    onlyLinksLength = onlyLinks.length,
    lastPage = props.pagination_data.last_page,
    currentPage : number = props.pagination_data.current_page,
    insertDots : TypeLink = {
        active : false,
        label : '',
        url : null,
        dots: '...'
    };

const index : number = onlyLinks.findIndex((data : TypeLink) : boolean => {
    return data.active;
});

if (currentPage <= 3 && onlyLinksLength >= 5) {
    if (currentPage == 1) {
        preparedLinksData.push(
            onlyLinks[index], // current
            onlyLinks[index + 1], // next
            onlyLinks[index + 2], // next next
            insertDots,
        );
    } else if (currentPage == 2) {
        preparedLinksData.push(
            onlyLinks[index - 1], // prev
            onlyLinks[index], // current
            onlyLinks[index + 1], // next
            insertDots,
        );
    } else {
        preparedLinksData.push(
            insertDots,
            onlyLinks[index - 1], // prev
            onlyLinks[index], // current
            onlyLinks[index + 1], // next
            insertDots,
        );
    }
} else if (currentPage <= 3 && onlyLinksLength - 1 <= 2) {
    preparedLinksData.push(
        onlyLinks[index], // first
        onlyLinks[index + 1], // second
    );
} else if (currentPage <= 3 && onlyLinksLength - 1 <= 3) {
    preparedLinksData.push(
        onlyLinks[index], // first
        onlyLinks[index + 1], // second
        onlyLinks[index + 2], // third
    );
} else if (currentPage <= 3 && onlyLinksLength - 1 <= 4) {
    preparedLinksData.push(
        onlyLinks[index], // first
        onlyLinks[index + 1], // second
        onlyLinks[index + 2], // third
        insertDots,
    );
} else if (currentPage == lastPage) {
    preparedLinksData.push(
        insertDots,
        onlyLinks[index - 2], // prev prev
        onlyLinks[index - 1], // prev
        onlyLinks[index], // current
    );
} else if (currentPage == lastPage - 1) {
    preparedLinksData.push(
        insertDots,
        onlyLinks[index - 1], // prev
        onlyLinks[index], // current
        onlyLinks[index + 1], // next
    );
} else {
    preparedLinksData.push(
        insertDots,
        onlyLinks[index - 1], // prev
        onlyLinks[index], // current
        onlyLinks[index + 1], // next
        insertDots,
    );
}
</script>

<template>
    <nav v-if="pagination_data.next_page_url || pagination_data.prev_page_url"
         class="flex items-center justify-center mt-7">
        <ul class="flex items-center flex-wrap -space-x-px h-10 text-base">
            <li v-if="pagination_data.first_page_url && pagination_data.current_page > 1">
                <a :href="pagination_data.first_page_url"
                   class="flex items-center justify-center h-10 ms-0 leading-tight text-gray-500
                         bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100
                         hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                         dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">

                    <svg class="w-8 h-8 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m17 16-4-4 4-4m-6 8-4-4 4-4"/>
                    </svg>
                </a>
            </li>

            <li v-if="pagination_data.prev_page_url">
                <a :href="pagination_data.prev_page_url"
                   class="flex items-center justify-center px-2 h-10 ms-0 leading-tight text-gray-500
                             bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100
                             hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                             dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">

                    <span class="sr-only">
                        {{ __('text_prev') }}
                    </span>

                    <svg class="w-4 h-4 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 1 1 5l4 4"/>
                    </svg>
                </a>
            </li>

            <template v-for="link_data in preparedLinksData">
                <li>
                    <span v-if="link_data.active"
                          aria-current="page"
                          class="z-10 flex items-center justify-center px-4 h-10 leading-tight
                                text-blue-600 border border-blue-300 bg-blue-50 hover:bg-blue-100
                                hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white">
                        {{ link_data.label }}
                    </span>

                    <span v-else-if="link_data.dots"
                          class="flex items-center justify-center px-2 h-10 leading-tight
                                 text-gray-500 bg-white border border-gray-300 hover:bg-gray-100
                                 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                                 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                        {{ link_data.dots }}
                    </span>

                    <a v-else
                       :href="link_data.url ?? ''"
                       class="flex items-center justify-center px-4 h-10 leading-tight
                             text-gray-500 bg-white border border-gray-300 hover:bg-gray-100
                             hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                             dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                        {{ link_data.label }}
                    </a>
                </li>
            </template>

            <li v-if="pagination_data.next_page_url">
                <a :href="pagination_data.next_page_url"
                   class="flex items-center justify-center px-2 h-10 leading-tight text-gray-500
                             bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100
                             hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                             dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">

                    <span class="sr-only">
                        {{ __('text_next') }}
                    </span>

                    <svg class="w-4 h-4 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 9 4-4-4-4"/>
                    </svg>
                </a>
            </li>

            <li v-if="pagination_data.last_page_url && pagination_data.last_page > pagination_data.current_page">
                <a :href="pagination_data.last_page_url"
                   class="flex items-center justify-center h-10 leading-tight text-gray-500
                             bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100
                             hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700
                             dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">

                    <span class="sr-only">
                        {{ __('text_next') }}
                    </span>

                    <svg class="w-8 h-8 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m7 16 4-4-4-4m6 8 4-4-4-4"/>
                    </svg>
                </a>
            </li>
        </ul>
    </nav>
</template>

<style scoped>

</style>
